/**
 * Utility for extracting links from conversation messages
 */

export interface ExtractedLink {
  url: string;
  title?: string;
  description?: string;
  timestamp: string;
  messageIndex: number;
  context?: string; // Surrounding text for context
}

export class LinkExtractor {
  // Comprehensive URL regex that matches various URL formats
  private static readonly URL_REGEX = /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gi;
  
  // Markdown link regex to extract title and URL
  private static readonly MARKDOWN_LINK_REGEX = /\[([^\]]*)\]\(([^)]+)\)/g;
  
  // HTML link regex
  private static readonly HTML_LINK_REGEX = /<a[^>]+href=["']([^"']+)["'][^>]*>([^<]*)<\/a>/gi;

  /**
   * Extract all links from a conversation message
   */
  static extractLinksFromMessage(
    content: string, 
    messageIndex: number, 
    timestamp: string
  ): ExtractedLink[] {
    const links: ExtractedLink[] = [];
    
    // Extract markdown links
    const markdownLinks = this.extractMarkdownLinks(content, messageIndex, timestamp);
    links.push(...markdownLinks);
    
    // Extract HTML links
    const htmlLinks = this.extractHtmlLinks(content, messageIndex, timestamp);
    links.push(...htmlLinks);
    
    // Extract plain URLs (excluding those already found in markdown/HTML)
    const existingUrls = new Set(links.map(link => link.url));
    const plainLinks = this.extractPlainUrls(content, messageIndex, timestamp)
      .filter(link => !existingUrls.has(link.url));
    links.push(...plainLinks);
    
    return links;
  }

  /**
   * Extract links from markdown format [title](url)
   */
  private static extractMarkdownLinks(
    content: string, 
    messageIndex: number, 
    timestamp: string
  ): ExtractedLink[] {
    const links: ExtractedLink[] = [];
    let match;
    
    // Reset regex to avoid issues with global flag
    this.MARKDOWN_LINK_REGEX.lastIndex = 0;
    
    while ((match = this.MARKDOWN_LINK_REGEX.exec(content)) !== null) {
      const title = match[1]?.trim();
      const url = this.normalizeUrl(match[2]?.trim());
      
      if (url && this.isValidUrl(url)) {
        const context = this.extractContext(content, match.index, 50);
        
        links.push({
          url,
          title: title || this.extractTitleFromUrl(url),
          timestamp,
          messageIndex,
          context
        });
      }
    }
    
    return links;
  }

  /**
   * Extract links from HTML format <a href="url">title</a>
   */
  private static extractHtmlLinks(
    content: string, 
    messageIndex: number, 
    timestamp: string
  ): ExtractedLink[] {
    const links: ExtractedLink[] = [];
    let match;
    
    // Reset regex to avoid issues with global flag
    this.HTML_LINK_REGEX.lastIndex = 0;
    
    while ((match = this.HTML_LINK_REGEX.exec(content)) !== null) {
      const url = this.normalizeUrl(match[1]?.trim());
      const title = match[2]?.trim();
      
      if (url && this.isValidUrl(url)) {
        const context = this.extractContext(content, match.index, 50);
        
        links.push({
          url,
          title: title || this.extractTitleFromUrl(url),
          timestamp,
          messageIndex,
          context
        });
      }
    }
    
    return links;
  }

  /**
   * Extract plain URLs from text
   */
  private static extractPlainUrls(
    content: string, 
    messageIndex: number, 
    timestamp: string
  ): ExtractedLink[] {
    const links: ExtractedLink[] = [];
    let match;
    
    // Reset regex to avoid issues with global flag
    this.URL_REGEX.lastIndex = 0;
    
    while ((match = this.URL_REGEX.exec(content)) !== null) {
      const url = this.normalizeUrl(match[0]?.trim());
      
      if (url && this.isValidUrl(url)) {
        const context = this.extractContext(content, match.index, 50);
        const description = this.extractDescriptionFromUrl(url);
        
        links.push({
          url,
          title: this.extractTitleFromUrl(url),
          description,
          timestamp,
          messageIndex,
          context
        });
      }
    }
    
    return links;
  }

  /**
   * Normalize URL by adding protocol if missing and cleaning trailing punctuation
   */
  private static normalizeUrl(url: string): string {
    if (!url) return '';
    
    // Remove common trailing punctuation that shouldn't be part of URLs
    url = url.replace(/[.,;:)\]}>"']+$/, '');
    
    // Add protocol if missing
    if (!url.match(/^https?:\/\//i)) {
      // Check if it looks like a domain (has a dot and valid TLD)
      if (url.match(/^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(\/.*)?$/)) {
        return `https://${url}`;
      }
    }
    
    return url;
  }

  /**
   * Validate if a string is a valid URL
   */
  private static isValidUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      return ['http:', 'https:'].includes(parsedUrl.protocol);
    } catch {
      return false;
    }
  }

  /**
   * Extract title from URL (domain name)
   */
  private static extractTitleFromUrl(url: string): string {
    try {
      // Special handling for Medicare LCD/NCD documents
      if (url.includes('cms.gov')) {
        if (url.includes('lcd.aspx')) {
          const lcdMatch = url.match(/lcdid=(\d+)/i);
          const lcdNum = lcdMatch ? lcdMatch[1] : '';
          return lcdNum ? `LCD ${lcdNum} - Medicare Coverage Document` : 'Medicare LCD Document';
        } else if (url.includes('ncd.aspx')) {
          const ncdMatch = url.match(/ncdid=(\d+)/i);
          const ncdNum = ncdMatch ? ncdMatch[1] : '';
          return ncdNum ? `NCD ${ncdNum} - Medicare Coverage Document` : 'Medicare NCD Document';
        }
      }
      
      // Default to domain name
      const parsedUrl = new URL(url);
      return parsedUrl.hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }

  /**
   * Extract description from URL (for special URLs like Medicare LCD/NCD)
   */
  private static extractDescriptionFromUrl(url: string): string | undefined {
    try {
      // Special handling for Medicare LCD/NCD documents
      if (url.includes('cms.gov')) {
        if (url.includes('lcd.aspx')) {
          const lcdMatch = url.match(/lcdid=(\d+)/i);
          const verMatch = url.match(/ver=(\d+)/i);
          const lcdNum = lcdMatch ? lcdMatch[1] : '';
          const version = verMatch ? verMatch[1] : '';
          return lcdNum ? `Local Coverage Determination (LCD) ${lcdNum}${version ? ` version ${version}` : ''} from Medicare Coverage Database` : undefined;
        } else if (url.includes('ncd.aspx')) {
          const ncdMatch = url.match(/ncdid=(\d+)/i);
          const verMatch = url.match(/ver=(\d+)/i);
          const ncdNum = ncdMatch ? ncdMatch[1] : '';
          const version = verMatch ? verMatch[1] : '';
          return ncdNum ? `National Coverage Determination (NCD) ${ncdNum}${version ? ` version ${version}` : ''} from Medicare Coverage Database` : undefined;
        }
      }
      return undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Extract surrounding context around a match
   */
  private static extractContext(text: string, index: number, contextLength: number): string {
    const start = Math.max(0, index - contextLength);
    const end = Math.min(text.length, index + contextLength);
    return text.slice(start, end).trim();
  }

  /**
   * Remove duplicate links based on URL
   */
  static deduplicateLinks(links: ExtractedLink[]): ExtractedLink[] {
    const seen = new Set<string>();
    return links.filter(link => {
      if (seen.has(link.url)) {
        return false;
      }
      seen.add(link.url);
      return true;
    });
  }

  /**
   * Group links by domain
   */
  static groupLinksByDomain(links: ExtractedLink[]): Map<string, ExtractedLink[]> {
    const grouped = new Map<string, ExtractedLink[]>();
    
    links.forEach(link => {
      try {
        const domain = new URL(link.url).hostname.replace(/^www\./, '');
        if (!grouped.has(domain)) {
          grouped.set(domain, []);
        }
        grouped.get(domain)!.push(link);
      } catch {
        // If URL parsing fails, group under 'other'
        if (!grouped.has('other')) {
          grouped.set('other', []);
        }
        grouped.get('other')!.push(link);
      }
    });
    
    return grouped;
  }

  /**
   * Filter links by domain patterns (e.g., medical sites)
   */
  static filterMedicalLinks(links: ExtractedLink[]): ExtractedLink[] {
    const medicalDomains = [
      'nih.gov',
      'cdc.gov',
      'who.int',
      'fda.gov',
      'cms.gov',
      'medlineplus.gov',
      'pubmed.ncbi.nlm.nih.gov',
      'ncbi.nlm.nih.gov',
      'webmd.com',
      'mayoclinic.org',
      'clevelandclinic.org',
      'hopkinsmedicine.org',
      'uptodate.com',
      'cochrane.org',
      'bmj.com',
      'nejm.org',
      'thelancet.com',
      'jama.jamanetwork.com',
      'nature.com',
      'sciencedirect.com'
    ];
    
    return links.filter(link => {
      try {
        const domain = new URL(link.url).hostname.toLowerCase();
        return medicalDomains.some(medDomain => 
          domain.includes(medDomain) || domain.endsWith(medDomain)
        );
      } catch {
        return false;
      }
    });
  }
}

export default LinkExtractor;